import { Button, Stack, Title, useActions, useToast } from "@introist/react-foundation/v2";
import { api } from "../../../../../services/rpc/RpcProvider";
import { useCallback, useState } from "react";
import { RuleEditorModal } from "../../../../../components/organisms/RuleEditorModal/RuleEditorModal";
import { Expression } from "@introist/introist-commons/dist";
import { PendingRemovalsModal } from "./PendingRemovalsModal";
import { formatDateTime } from "../../../../../utils/DatesUtils";
import { DateFormats } from "../../../../../utils/dates";

export const EmployeeDataRemovalConfig = () => {
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const { data: deletionRule, refetch } = api.employees.v4.deletion.rule.get.useQuery({});
  const { data: pendingRequests, refetch: refetchRequests } =
    api.employees.v4.deletion.requests.list.useQuery({ status: "pending" });

  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);

  const upsert = api.employees.v4.deletion.rule.upsert.useMutation();
  const remove = api.employees.v4.deletion.rule.delete.useMutation();

  const onSave = useCallback(
    async (rule: Expression) => {
      await upsert
        .mutateAsync({ rule })
        .then(async () => {
          await refetch();
          toast.success("Employee data removal rule saved");
          setRuleModalOpen(false);
        })
        .catch(() => {
          toast.error("Failed to save employee data removal rule");
        });
    },
    [upsert, toast, refetch]
  );

  const handleDelete = useCallback(async () => {
    await remove
      .mutateAsync({})
      .then(async () => {
        await refetch();
        setRuleModalOpen(false);
        toast.success("Employee data removal rule deleted");
      })
      .catch(() => {
        toast.error("Failed to delete employee data removal rule");
      });
  }, [remove, toast, refetch]);

  const onDelete = onConfirmAction(handleDelete, {
    title: "Delete employee data removal rule",
    description: "Are you sure you want to delete this rule?",
    confirmTitle: "Delete"
  });

  const pendingCount = pendingRequests?.length ?? 0;

  const hasRule = !!deletionRule?.rule;

  return (
    <Stack vertical gap="medium">
      <div>
        {hasRule && (
          <Button variant="outlined" onClick={() => setRuleModalOpen(true)}>
            Edit employee auto-removal rule
          </Button>
        )}
      </div>
      {!hasRule && (
        <Button variant="outlined" onClick={() => setRuleModalOpen(true)}>
          Set up employee auto-removal
        </Button>
      )}

      {hasRule && (
        <Stack vertical>
          <Stack style={{ marginTop: "var(--spacing-medium)" }} gap="small">
            <Title>
              {pendingCount === 0
                ? "No employees pending removal"
                : `${pendingCount} ${
                    pendingCount === 1 ? "employee" : "employees"
                  } pending removal`}
            </Title>
            {pendingCount > 0 && (
              <Button size="small" variant="outlined" onClick={() => setPendingModalOpen(true)}>
                Show
              </Button>
            )}
          </Stack>
          {deletionRule?.lastProcessedAt && (
            <Title>
              Last checked at{" "}
              {formatDateTime(deletionRule.lastProcessedAt, DateFormats.datetime.short)}
            </Title>
          )}
        </Stack>
      )}

      <RuleEditorModal
        open={ruleModalOpen}
        setOpen={setRuleModalOpen}
        expression={deletionRule?.rule ?? { and: [] }}
        onSave={async expression => {
          await onSave(expression);
          setRuleModalOpen(false);
        }}
        onDeleteRule={onDelete}
        title="Employee data removal rule"
      />

      <PendingRemovalsModal
        open={pendingModalOpen}
        onClose={() => setPendingModalOpen(false)}
        pendingRequests={pendingRequests ?? []}
        onCancelAll={async () => {
          await refetchRequests();
        }}
      />
    </Stack>
  );
};
